// Pawn item types — CHAIN, BANGLE, RING… per metal (backend migration 034).
//
// Shared by the Item Type master (PawnItemTypeMaster.js) and the Payment
// screen's ornament picker (paymentScreen/ItemTypePicker.js), so the two can
// never disagree on what a name or a code looks like.
//
// normalizeItemName / normalizeShortCode / suggestShortCode are a twin of the
// backend's utils/pawnItemTypes.js — change one, change both. The backend
// re-normalises everything it is sent, so a drift would not corrupt data, but
// the code suggested here would stop matching the one that gets saved.
import axios from "axios";

export const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

export const NAME_MAX = 100;
export const CODE_MAX = 20;

// "  nose   pin " → "NOSE PIN"
export const normalizeItemName = (value) =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase()
    .slice(0, NAME_MAX);

// Letters and digits only — typed at speed into a search box.
export const normalizeShortCode = (value) =>
  String(value ?? "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, CODE_MAX);

// NOSE PIN → NP, CHAIN → CHN, BANGLE → BNG; RNG, RNG2… when taken.
// `taken` is a Set of upper-case codes already used in the same branch.
export const suggestShortCode = (name, taken = new Set()) => {
  const words = normalizeItemName(name)
    .replace(/[^A-Z0-9 ]/g, "")
    .split(" ")
    .filter(Boolean);
  if (!words.length) return "";

  let base;
  if (words.length > 1) {
    base = words.map((w) => w[0]).join("").slice(0, 4);
  } else {
    const w = words[0];
    base = (w[0] + w.slice(1).replace(/[AEIOU]/g, "")).slice(0, 3);
    if (base.length < 2) base = w.slice(0, 3);
  }
  base = normalizeShortCode(base);

  if (!taken.has(base)) return base;
  for (let n = 2; n < 1000; n += 1) {
    const candidate = `${base.slice(0, CODE_MAX - String(n).length)}${n}`;
    if (!taken.has(candidate)) return candidate;
  }
  return "";
};

// Codes already held in one branch — deleted rows too, the DB key counts them.
export const takenCodesIn = (itemTypes, branchId) =>
  new Set(
    (itemTypes || [])
      .filter((t) => branchId == null || Number(t.branch_id) === Number(branchId))
      .map((t) => String(t.short_code || "").toUpperCase()),
  );

// How well an item type matches what the teller typed, best first; null = no
// match. A short code is a deliberate key, so an exact code beats everything —
// typing "CH" must land on CHAIN even when "CHAIN" and "CHOKER" both start
// with it.
export const matchRank = (itemType, query) => {
  const q = String(query || "").trim().toUpperCase();
  if (!q) return 5;
  const name = String(itemType.name || "").toUpperCase();
  const code = String(itemType.short_code || "").toUpperCase();
  if (code === q) return 0;
  if (name === q) return 1;
  if (code.startsWith(q)) return 2;
  if (name.startsWith(q)) return 3;
  if (name.split(" ").some((w) => w.startsWith(q))) return 4;
  if (name.includes(q)) return 6;
  return null;
};

// Items of one metal that match, best first, then alphabetical.
export const rankItemTypes = (itemTypes, query) =>
  (itemTypes || [])
    .map((t) => ({ t, r: matchRank(t, query) }))
    .filter((x) => x.r !== null)
    .sort((a, b) => a.r - b.r || String(a.t.name).localeCompare(String(b.t.name)))
    .map((x) => x.t);

// ─── API ────────────────────────────────────────────────────────────────────
// The /api/pawn routes carry no auth middleware — the acting user travels in
// the body as user_id, as everywhere else in the pawn module.
const base = (storeId) => `${API_BASE_URL}/api/pawn/item-types/${storeId}`;

export const fetchItemTypes = async (storeId, { status = "all", metalTypeId } = {}) => {
  const res = await axios.get(base(storeId), {
    params: { status, ...(metalTypeId ? { metal_type_id: metalTypeId } : {}) },
  });
  return { rows: res.data?.data || [], counts: res.data?.counts || {} };
};

export const createItemType = async (storeId, payload) => {
  const res = await axios.post(base(storeId), payload);
  return res.data; // { data, restored, message }
};

export const updateItemType = async (storeId, id, payload) => {
  const res = await axios.put(`${base(storeId)}/${id}`, payload);
  return res.data;
};

export const deleteItemType = async (storeId, id, userId) => {
  const res = await axios.delete(`${base(storeId)}/${id}`, {
    data: { user_id: userId },
  });
  return res.data;
};

export const restoreItemType = async (storeId, id, userId) => {
  const res = await axios.post(`${base(storeId)}/${id}/restore`, {
    user_id: userId,
  });
  return res.data;
};
